import { flattenThreadReplies } from '../message-order';
import { MessageWriteReview } from './MessageWriteReview';
import { FailedMessageActions } from './FailedMessageActions';
import { ChatLiteralCopy } from './ChatLiteralCopy';
import { useChatViewOwner } from '../hooks/useChatViewOwner';
import { ChatReadFailure } from './ChatReadFailure';
import { useRichChatClock, richChatText, richChatCount, richChatNames } from '../view-copy';
import { NamespaceBoundary } from '@/lib/i18n/NamespaceBoundary';
import { ChatTypingLabel } from './ChatTypingLabel';
import { localizedErrorMessage } from '@/lib/i18n/errors';
import { useTranslation } from '@/lib/i18n/react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import {
  X,
  Send,
  Minus,
  Maximize2,
  Paperclip,
  Smile,
  AlertCircle,
  Loader2,
  ArrowLeft,
  MessageSquareText,
  Copy,
  Mic,
  Pin,
  Reply,
  Check,
  CheckCheck,
} from 'lucide-react';
import {
  teamChatQueries,
  flattenMessagePages,
  useSendChannelMessage,
  useMarkChannelRead,
  useTogglePin,
  useToggleReaction,
  type ChannelMessage,
  type ChannelMessagesInfiniteData,
} from '../queries';
import { sortMessagesAsc } from '../message-order';
import { mergeFailedIntoFeed } from '../optimistic-send';
import { useFailedMessages } from '../failed-outbox';
import { useChannelFileUpload } from '../hooks/useChannelFileUpload';
import { isMediaPlaceholderCaption } from '../attachment-media';
import { SystemMessageLine } from './SystemMessageLine';
import { formatDue, statusLabel, type ThreadTracking } from '../thread-tracking';
import { useChatDockStore } from '../chat-dock-store';
import { useAuthStore } from '@/store/auth-store';
import { colorFromName, initialsFromName } from '@/components/ui-kit';
import { cn } from '@/lib/utils/cn';
import { TeamChatMedia } from './TeamChatMedia';
import { renderTextWithMentions } from '@/lib/utils/render-mentions';
import { channelReplyExcerpt, channelReplyPrefix } from '../channel-reply';
import { useVoiceNoteRecorder } from '../hooks/useVoiceNoteRecorder';
import { VoiceRecordingBar } from './VoiceRecordingBar';
import { realtimeManager } from '@/lib/realtime/realtime-manager';
import { useChannelTypers } from '@/lib/realtime/typing-store';
import {
  latestOwnReadReceipt,
  type DockReadReceipt,
} from '../dock-presence';

interface DockChatWindowProps {
  channelId: string;
  channelName: string;
  isDirect?: boolean;
  onClose: () => void;
  /** TC-21: collapse this window to a chip next to the launcher. */
  onMinimize: () => void;
}

// TC-20b: lightweight emoji row for the dock composer. ChannelView's
// QUICK_REACTIONS palette is module-private (and reaction-oriented), so the
// dock keeps its own 8 frequent emojis — same "no 200KB emoji lib" tradeoff.
const DOCK_EMOJIS = ['👍', '❤️', '😂', '🎉', '✅', '👀', '🚀', '🙏'] as const;

/**
 * Compact floating chat window for the global team-chat dock — header + message
 * list + composer for ONE channel/DM. Reuses the same queries as the full
 * Team-Chat page, so the realtime `message.new` invalidation keeps it live with
 * zero extra wiring. TC-15: reads `messagesInfinite` (the SAME cache ChannelView
 * pages through) — the dock only ever shows the first page (no infinite-scroll
 * UI here), but sharing the key means no duplicate fetch when both surfaces are
 * open.
 */
export function DockChatWindow(props: DockChatWindowProps) {
  const owner = useChatViewOwner();
  return <NamespaceBoundary namespace="teamchat"><RichDockChatWindow key={owner + '|' + props.channelId} {...props} /></NamespaceBoundary>;
}

function RichDockChatWindow({ channelId, channelName, isDirect, onClose, onMinimize }: DockChatWindowProps) {
  const { locale, t: chatText } = useTranslation('teamchat');
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { data: messagesData, isLoading, isError, error, refetch, isFetching } = useInfiniteQuery(
    teamChatQueries.messagesInfinite(channelId),
  );
  // Cast: useInfiniteQuery widens TPageParam to `unknown` even though
  // `messagesInfinite` declares `string | null` — the cache shape is the
  // documented ChannelMessagesInfiniteData either way.
  const messages = React.useMemo(
    () => flattenMessagePages(messagesData as ChannelMessagesInfiniteData | undefined),
    [messagesData],
  );
  const send = useSendChannelMessage();
  const markRead = useMarkChannelRead();
  const markReadMutate = markRead.mutate;
  const formatClock = useRichChatClock();
  const upload = useChannelFileUpload(channelId);

  // Same cache entry the CommsHub already observes — no extra fetch. We only
  // need this channel's unread_count to decide when to mark-as-read.
  const { data: channels } = useQuery(teamChatQueries.channels());
  // The backend persists per-member last_read_at but does not broadcast it
  // yet. Poll only while this expanded dock is mounted; a future channel.read
  // event also invalidates this same key for an immediate refresh.
  const { data: channelMembers } = useQuery({
    ...teamChatQueries.members(channelId),
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
  });
  const unreadCount = React.useMemo(
    () => channels?.find((c) => c.id === channelId)?.unread_count ?? 0,
    [channels, channelId],
  );
  const isArchived = !!channels?.find((c) => c.id === channelId)?.is_archived;

  const [text, setText] = React.useState('');
  const [replyTarget, setReplyTarget] = React.useState<ChannelMessage | null>(null);
  const [emojiOpen, setEmojiOpen] = React.useState(false);
  // Mini-hilo (TC-dock-threads): cuando está seteado, la ventana muestra el
  // hilo de ese mensaje padre en lugar del feed del canal. La instancia se
  // monta con key={channelId} (CommsHub), así que el estado no se filtra
  // entre canales.
  const [threadParentId, setThreadParentId] = React.useState<string | null>(null);
  const emojiRef = React.useRef<HTMLDivElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  // True while the user is scrolled within 80px of the bottom — updated on
  // every scroll so the auto-stick effect knows whether to follow new messages.
  const nearBottomRef = React.useRef(true);
  const voiceRecorder = useVoiceNoteRecorder({
    resourceId: channelId,
    onRecorded: (file) => {
      const caption = replyTarget ? channelReplyPrefix(replyTarget).trim() : '';
      setReplyTarget(null);
      void upload.uploadFiles([file], caption);
    },
  });

  React.useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 128)}px`;
  }, [text]);

  // FIX-1: failed sends live in the failed-outbox store (NOT the query cache,
  // where any refetch would wipe them) and are merged into the rendered feed
  // here — same pattern as ChannelView. Their Reintentar/Descartar buttons
  // (DockMessage) operate against the outbox via useRetryChannelMessage.
  const failedMessages = useFailedMessages(channelId);

  // The backend returns messages newest-first (DESC); the dock renders
  // oldest→newest like every chat surface. Single source of truth for the
  // ordering lives in ../message-order. Failed outbox rows append at the end,
  // by the composer where the send happened.
  const ordered = React.useMemo(
    () => mergeFailedIntoFeed(sortMessagesAsc(messages), failedMessages),
    [messages, failedMessages],
  );
  const latestReceipt = React.useMemo(
    () => latestOwnReadReceipt(ordered, channelMembers ?? [], currentUserId, !!isDirect, locale),
    [ordered, channelMembers, currentUserId, isDirect, locale],
  );
  const lastMessage = ordered.length > 0 ? ordered[ordered.length - 1] : undefined;
  const lastIsMine = !!lastMessage && lastMessage.user_id === currentUserId;
  const lastMessageId = lastMessage?.id;

  // Stick to the newest message when the thread grows — but only if the user
  // was already reading the bottom (<80px away) or the newest message is their
  // own (they just sent it). Never yank them away from scrolled-up history.
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (nearBottomRef.current || lastIsMine) el.scrollTop = el.scrollHeight;
  }, [ordered.length, lastIsMine]);

  // Close the emoji popover on outside click (same pattern as ChannelView's
  // reaction palette).
  React.useEffect(() => {
    if (!emojiOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) {
        setEmojiOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [emojiOpen]);

  // Mark the channel as read on mount and whenever new messages land while the
  // window is open and the document is visible. Gated on unread_count > 0 (and
  // a 2s throttle) so we don't spam the endpoint; the mutation's onSuccess
  // invalidates ['team-chat','channels'], which zeroes unread_count until the
  // next inbound message re-raises it.
  //
  // FIX-4: the throttle used to be leading-edge only — a second message inside
  // the 2s window fired nothing, leaving the badge stuck while the window sat
  // open. A run landing inside the window now schedules the TRAILING edge
  // (setTimeout for the remainder), which re-checks visibility before marking;
  // the cleanup clears the timer (unreadCount → 0 after the mark-read refetch
  // re-runs the effect and cancels a now-pointless re-fire). We also listen
  // for visibilitychange so unread accumulated while the tab was hidden clears
  // the moment the user comes back.
  const lastMarkAtRef = React.useRef(0);
  React.useEffect(() => {
    // `lastMessageId` is a dependency so a message arriving while the window is
    // open re-runs this even before the channels list refetch bumps the count.
    void lastMessageId;
    if (unreadCount <= 0) return;
    let timer: number | undefined;
    const fire = () => {
      if (document.visibilityState !== 'visible') return;
      const remaining = 2000 - (Date.now() - lastMarkAtRef.current);
      if (remaining > 0) {
        window.clearTimeout(timer);
        timer = window.setTimeout(fire, remaining);
        return;
      }
      lastMarkAtRef.current = Date.now();
      markReadMutate(channelId);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fire();
    };
    document.addEventListener('visibilitychange', onVisibility);
    fire();
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [unreadCount, lastMessageId, channelId, markReadMutate]);

  // TC-29: no `send.isPending` gate — the optimistic bubble carries the
  // in-flight state, so rapid consecutive sends just pipeline.
  const submit = () => {
    const content = text.trim();
    if (!content || isArchived) return;
    const outgoingContent = replyTarget
      ? `${channelReplyPrefix(replyTarget)}${content}`
      : content;
    setText('');
    setReplyTarget(null);
    send.mutate({ channelId, content: outgoingContent });
  };

  // TC-20d: jump from the dock to the full Chat Interno page with this channel
  // selected. CommsHub mounts OUTSIDE RouterProvider, so router hooks aren't
  // available here — we stash the target in the dock store (TeamChatPage reads
  // `openInFullView` as its initial-channel override) and push `/chat?ch=<id>`
  // + a synthetic popstate so TanStack Router picks up the URL change (the
  // `?ch=` param doubles as the target on a hard reload, matching the
  // copy-link format ChannelView already emits).
  const openInFullView = () => {
    // Desde el mini-hilo, la vista completa abre ESE hilo (no sólo el canal).
    useChatDockStore.getState().setOpenInFullView(channelId, threadParentId);
    onClose();
    window.history.pushState(null, '', `/chat?ch=${encodeURIComponent(channelId)}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  // Padre del mini-hilo — la fila ya vive en el feed cargado; el fallback del
  // query del hilo lo cubre DockThreadView si el feed aún no lo trae.
  const threadParent = threadParentId
    ? ordered.find((m) => m.id === threadParentId)
    : undefined;
  const threadTitle =
    threadParent?.tracking?.title?.trim() ||
    (threadParent?.content ?? '').trim().replace(/\s+/g, ' ').slice(0, 40) ||
    richChatText("view.thread");

  return (
    <div className="flex h-[min(38rem,calc(100vh-6rem))] w-[min(25rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-lg border border-white/80 bg-white shadow-[0_26px_70px_rgba(28,20,56,0.22)] ring-1 ring-border-subtle/70">
      <MessageWriteReview channelId={channelId} />
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-border-subtle bg-white/95 px-3 py-2.5 backdrop-blur">
        {threadParentId ? (
          <button
            type="button"
            onClick={() => setThreadParentId(null)}
            className="flex min-w-0 items-center gap-2 text-fg-1 transition-colors hover:text-blaze-violet"
            title={richChatText("view.backToChannel")}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-blaze-violet/10 text-blaze-violet">
              <ArrowLeft size={14} />
            </span>
            <span className="min-w-0 text-left">
              <span className="block truncate text-sm font-bold"><bdi dir="auto">{threadTitle}</bdi></span>
              <span className="block text-[9px] font-bold uppercase tracking-wide text-fg-3">{richChatText("view.thread")}</span>
            </span>
          </button>
        ) : (
          <span className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-blaze-violet/10 text-sm font-extrabold text-blaze-violet">
              {isDirect ? '@' : '#'}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-extrabold text-fg-1"><bdi dir="auto">{channelName}</bdi></span>
              <span className="block text-[9px] font-bold uppercase tracking-wide text-fg-3">
                {isDirect ? richChatText("view.shared.common.comms.direct") : richChatText("view.shared.common.comms.teamChannel")}
              </span>
            </span>
          </span>
        )}
        <span className="flex items-center gap-0.5 flex-shrink-0">
          <HeaderBtn onClick={openInFullView} title={richChatText("view.openInTeamChat")}>
            <Maximize2 size={13} />
          </HeaderBtn>
          <HeaderBtn onClick={onMinimize} title={richChatText("view.minimize")}>
            <Minus size={14} />
          </HeaderBtn>
          <HeaderBtn onClick={onClose} title={richChatText("view.shared.apps.manage.close")}>
            <X size={14} />
          </HeaderBtn>
        </span>
      </div>

      {threadParentId ? (
        <DockThreadView
          channelId={channelId}
          parentMessageId={threadParentId}
          parentFromFeed={threadParent}
          currentUserId={currentUserId}
        />
      ) : (
        <>
      {/* Messages */}
      <div
        ref={scrollRef}
        onScroll={() => {
          const el = scrollRef.current;
          if (el) nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
        className="flex-1 space-y-2 overflow-y-auto bg-gradient-to-b from-paper/80 to-white px-3 py-3"
      >
        {isLoading ? (
          <div className="flex justify-center py-6">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-blaze-violet border-t-transparent" />
          </div>
        ) : isError ? (
          <ChatReadFailure error={error} retry={() => void refetch()} pending={isFetching} />
        ) : ordered.length === 0 ? (
          <p className="text-xs text-fg-3 text-center py-6">{richChatText("view.noMessages")}</p>
        ) : (
          ordered.map((m) =>
            m.message_type === 'system' ? (
              /* Audit trail de tracking (#93544 gotcha 4) — línea centrada. */
              <SystemMessageLine key={m.id} content={m.content} copy={m.system_copy} />
            ) : (
              <DockMessage
                key={m.id}
                message={m}
                mine={m.user_id === currentUserId}
                time={m.created_at ? formatClock(m.created_at) : ''}
                channelId={channelId}
                readReceipt={
                  latestReceipt?.messageId === m.id ? latestReceipt?.receipt : undefined
                }
                onOpenThread={setThreadParentId}
                onReplyInChannel={setReplyTarget}
              />
            ),
          )
        )}
      </div>

      {isArchived && (
        <div className="border-t border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-800">
           {richChatText("view.archivedHistory")} </div>
      )}

      {/* Upload indicator — one row per in-flight/errored file (TC-20a). Sent
          files leave `pending` on their own; errors stay until dismissed. */}
      {upload.pending.length > 0 && (
        <div className="px-2 py-1.5 border-t border-border-subtle bg-white flex flex-col gap-1 flex-shrink-0">
          {upload.pending.map((entry) => (
            <div key={entry.id} className="flex items-center gap-1.5 text-[11px] min-w-0">
              {entry.status === 'error' ? (
                <AlertCircle size={12} className="text-red-500 flex-shrink-0" />
              ) : (
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-blaze-violet border-t-transparent flex-shrink-0" />
              )}
              <span className={cn('flex-1 truncate', entry.status === 'error' ? 'text-red-600' : 'text-fg-2')}>
                {entry.status === 'error' ? localizedErrorMessage(entry.error, 'common', 'upload.unconfirmed') : entry.name}
              </span>
              {entry.status === 'error' && (
                <button
                  type="button"
                  onClick={() => upload.dismiss(entry.id)}
                  className="h-4 w-4 flex items-center justify-center rounded-xs text-fg-3 hover:text-fg-1 flex-shrink-0"
                  title={richChatText("view.shared.inbox.proposal.dismiss")}
                >
                  <X size={11} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <DockTypingLine channelId={channelId} />

      {/* Composer */}
      <div className="relative shrink-0 border-t border-border-subtle bg-white p-3">
        {/* Emoji popover (TC-20b) */}
        {emojiOpen && (
          <div
            ref={emojiRef}
            className="absolute bottom-full left-2 z-10 mb-1 flex gap-0.5 rounded-sm border border-border-subtle bg-white px-1.5 py-1 shadow-xl"
          >
            {DOCK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => {
                  setText((t) => t + emoji);
                  setEmojiOpen(false);
                }}
                className="h-7 w-7 flex items-center justify-center rounded-xs text-base hover:bg-paper transition-colors"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
        {replyTarget && (
          <div className="mb-2 flex items-start gap-2 rounded-sm border-l-2 border-blaze-violet bg-blaze-violet/5 px-2.5 py-2">
            <Reply size={13} className="mt-0.5 flex-shrink-0 text-blaze-violet" />
            <span className="min-w-0 flex-1">
              <span className="block text-[10px] font-bold uppercase tracking-wide text-blaze-violet">
                 <ChatLiteralCopy copyKey="view.replyingChannelNamed" values={{ name: replyTarget.user_name ?? richChatText('view.thisMessage') }} />
              </span>
              <span className="block truncate text-xs text-fg-2">
                {channelReplyExcerpt(replyTarget)}
              </span>
            </span>
            <button
              type="button"
              onClick={() => setReplyTarget(null)}
              className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-xs text-fg-3 hover:bg-white hover:text-fg-1"
              title={richChatText("view.shared.inbox.composer.cancelReply")}
            >
              <X size={12} />
            </button>
          </div>
        )}
        <div className="rounded-md border border-border-subtle bg-paper/45 p-1.5 transition-all focus-within:border-blaze-violet focus-within:bg-white focus-within:ring-2 focus-within:ring-blaze-violet/10">
          {(voiceRecorder.isRecording || voiceRecorder.isStarting) ? (
            <VoiceRecordingBar
              duration={voiceRecorder.duration}
              onCancel={voiceRecorder.cancel}
              onStop={voiceRecorder.stop}
              isStarting={voiceRecorder.isStarting}
              stopLabel={chatText('voice.send')}
            />
          ) : (
            <textarea
              ref={textareaRef}
              value={text}
              disabled={isArchived}
              onChange={(e) => {
                setText(e.target.value);
                realtimeManager.sendChannelTyping(channelId);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              rows={1}
              placeholder={
                isArchived
                  ? richChatText("view.archivedReadOnly")
                  : richChatText(isDirect ? 'view.messageDirect' : 'view.messageChannel', { name: channelName })
              }
              className="max-h-32 min-h-12 w-full resize-none bg-transparent px-2.5 py-2 text-sm leading-5 text-fg-1 outline-none placeholder:text-fg-3 disabled:cursor-not-allowed"
            />
          )}

          <div className="flex items-center justify-between gap-2 px-1 pb-0.5 pt-1">
            <span className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => setEmojiOpen((v) => !v)}
                disabled={isArchived || (voiceRecorder.isRecording || voiceRecorder.isStarting)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-fg-3 transition-colors hover:bg-paper hover:text-fg-1 disabled:opacity-35"
                title={richChatText("view.emoji")}
              >
                <Smile size={16} />
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={upload.isUploading || isArchived || (voiceRecorder.isRecording || voiceRecorder.isStarting)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-fg-3 transition-colors hover:bg-paper hover:text-fg-1 disabled:opacity-35"
                title={upload.isUploading ? richChatText("view.shared.bots.form.uploading") : richChatText("view.attachFile")}
              >
                <Paperclip size={15} />
              </button>
              <button
                type="button"
                onClick={() => void voiceRecorder.start()}
                disabled={upload.isUploading || isArchived || (voiceRecorder.isRecording || voiceRecorder.isStarting)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-fg-3 transition-colors hover:bg-paper hover:text-blaze-violet disabled:opacity-35"
                title={chatText(voiceRecorder.isStarting ? 'voice.requesting' : 'voice.record')}
              >
                <Mic size={16} />
              </button>
            </span>
            <span className="flex items-center gap-2">
              <span className="hidden text-[9px] font-semibold text-fg-3 sm:inline">{richChatText("view.enterSends")}</span>
              <button
                type="button"
                onClick={submit}
                disabled={!text.trim() || isArchived || (voiceRecorder.isRecording || voiceRecorder.isStarting)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-blaze-violet text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow disabled:translate-y-0 disabled:opacity-35"
                title={richChatText("view.shared.inbox.composer.keyboardSend")}
              >
                <Send size={16} />
              </button>
            </span>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = e.target.files;
            if (files && files.length > 0) {
              const draft = text.trim();
              const caption = replyTarget
                ? `${channelReplyPrefix(replyTarget)}${draft}`.trim()
                : draft;
              setText('');
              setReplyTarget(null);
              void upload.uploadFiles(files, caption);
            }
            e.target.value = '';
          }}
        />
        {voiceRecorder.error && (
          <p className="mt-1.5 text-[11px] font-semibold text-red-600">{voiceRecorder.error}</p>
        )}
      </div>
        </>
      )}
    </div>
  );
}

// Registros estáticos (regla del repo: Tailwind JIT no ve `bg-${color}`).
const TRACKING_DOT: Record<string, string> = {
  open: 'bg-blaze-blue',
  in_progress: 'bg-blaze-orange',
  resolved: 'bg-blaze-green',
};
const DUE_TONE_TEXT: Record<string, string> = {
  overdue: 'text-blaze-magenta',
  soon: 'text-blaze-orange',
  ok: 'text-fg-3',
};

/**
 * Mini-hilo del dock: la misma ventanita alterna a la vista del hilo
 * (padre + replies + composer de texto) sin salir de la burbuja — patrón
 * Slack. Comparte el query de hilo por owner/revision con ThreadPanel;
 * los hints propios disparan GET antes de mostrar historia, así que
 * es vivo con cero wiring extra). El composer es SOLO texto: adjuntos y
 * reacciones viven en la vista completa (useChannelFileUpload omite
 * parent_message_id — misma limitación documentada en ThreadPanel).
 */
function DockThreadView({
  channelId,
  parentMessageId,
  parentFromFeed,
  currentUserId,
}: {
  channelId: string;
  parentMessageId: string;
  /** Fila del padre ya presente en el feed del dock — evita el flash de
   *  carga; el query del hilo la reemplaza/complementa al llegar. */
  parentFromFeed?: ChannelMessage;
  currentUserId?: string;
}) {
  const { data: threadData, isLoading, isError, error, refetch, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(teamChatQueries.threadInfinite(parentMessageId));
  const formatClock = useRichChatClock();
  const send = useSendChannelMessage();
  const [text, setText] = React.useState('');
  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Never retain remote history after a failed owned read, including revocation.
  const parent = isError ? undefined : threadData?.pages[0]?.parent ?? parentFromFeed ?? undefined;
  const tracking: ThreadTracking | null | undefined = parent?.tracking;
  const due = formatDue(tracking?.due_at);

  const localReplies=useFailedMessages(channelId,parentMessageId);
  const replies=mergeFailedIntoFeed(isError?[]:flattenThreadReplies(threadData),localReplies);

  // El mini-hilo abre anclado al final (lo nuevo) y sigue las replies.
  const newestReplyId=replies.at(-1)?.id;
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [newestReplyId]);

  const submit = () => {
    const content = text.trim();
    if (!content) return;
    setText('');
    send.mutate({ channelId, content, parentMessageId });
  };

  return (
    <>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-2 bg-paper">
        {/* Línea de tracking compacta — lectura; se edita en vista completa. */}
        {tracking && (
          <div
            className="flex items-center gap-1.5 flex-wrap rounded-sm border border-border-subtle bg-white px-2 py-1.5 text-[11px]"
            title={richChatText("view.editTrackingInFullView")}
          >
            <span
              className={cn(
                'h-2 w-2 rounded-full flex-shrink-0',
                TRACKING_DOT[tracking.status ?? 'open'] ?? 'bg-fg-3',
              )}
            />
            <span className="font-bold text-fg-1">{statusLabel(tracking.status)}</span>
            {tracking.priority === 'high' && (
              <span className="font-semibold text-blaze-magenta">· {richChatText('priority.high')}</span>
            )}
            {tracking.assignee_name && (
              <span className="text-fg-3 truncate">· <bdi dir="auto">{tracking.assignee_name}</bdi></span>
            )}
            {due && (
              <span className={cn('font-semibold', DUE_TONE_TEXT[due.tone])}>· {due.label}</span>
            )}
          </div>
        )}

        {/* Mensaje padre */}
        {parent ? (
          parent.message_type === 'system' ? (
            <SystemMessageLine content={parent.content} copy={parent.system_copy} />
          ) : (
            <DockMessage
              message={parent}
              mine={parent.user_id === currentUserId}
              time={parent.created_at ? formatClock(parent.created_at) : ''}
              channelId={channelId}
            />
          )
        ) : isLoading ? (
          <div className="flex justify-center py-4">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-blaze-violet border-t-transparent" />
          </div>
        ) : null}

        {isError && <ChatReadFailure error={error} retry={() => void refetch()} pending={isFetching} />}
        {/* Separador padre/replies */}
        <div className="flex items-center gap-2 text-[10px] font-semibold text-fg-3 uppercase tracking-wide">
          <span className="h-px flex-1 bg-border-subtle" />
          {replies.length > 0
            ? richChatText('view.repliesCount', { count: Math.max(parent?.reply_count??0,replies.length) })
            : richChatText("view.shared.dashboard.csat.noResponses")}
          <span className="h-px flex-1 bg-border-subtle" />
        </div>

        {hasNextPage&&!isError&&<button type="button" disabled={isFetchingNextPage} onClick={()=>{
          const el=scrollRef.current,height=el?.scrollHeight??0,top=el?.scrollTop??0;
          void fetchNextPage().then(()=>requestAnimationFrame(()=>{if(el)el.scrollTop=top+el.scrollHeight-height;}));
        }} className="mb-2 min-h-9 rounded-md border border-border-subtle px-3 text-xs text-fg-2">{richChatText('view.loadOlderReplies')}</button>}
        {replies.map((r) =>
          r.message_type === 'system' ? (
            <SystemMessageLine key={r.id} content={r.content} copy={r.system_copy} />
          ) : (
            <DockMessage
              key={r.id}
              message={r}
              mine={r.user_id === currentUserId}
              time={r.created_at ? formatClock(r.created_at) : ''}
              channelId={channelId}
            />
          ),
        )}
      </div>

      <DockTypingLine channelId={channelId} />

      {/* Composer del hilo — sólo texto */}
      <div className="p-2 border-t border-border-subtle bg-white flex items-end gap-1 flex-shrink-0">
        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            realtimeManager.sendChannelTyping(channelId);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          rows={1}
          placeholder={richChatText("view.replyThreadPlaceholder")}
          className="flex-1 resize-none max-h-24 px-2.5 py-1.5 text-sm rounded-sm border border-border-subtle bg-paper/40 focus:bg-white focus:border-blaze-violet focus:outline-none transition-colors min-w-0"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!text.trim()}
          className="h-8 w-8 flex items-center justify-center rounded-sm bg-blaze-violet text-white hover:opacity-90 disabled:opacity-40 transition-opacity flex-shrink-0"
          title={richChatText("view.shared.inbox.composer.reply")}
        >
          <Send size={15} />
        </button>
      </div>
    </>
  );
}

function HeaderBtn({ onClick, title, children }: { onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-6 w-6 flex items-center justify-center rounded-sm text-fg-3 hover:bg-paper hover:text-fg-1 transition-colors"
      title={title}
    >
      {children}
    </button>
  );
}

function DockTypingLine({ channelId }: { channelId: string }) {
  const typers = useChannelTypers(channelId);
  if (typers.length === 0) return null;
  const label = <ChatTypingLabel names={typers.map((typer) => typer.userName)} />;

  return (
    <div
      aria-live="polite"
      className="flex shrink-0 items-center gap-2 border-t border-border-subtle bg-white px-3 py-1.5 text-[10px] text-fg-2 [overflow-wrap:anywhere]"
    >
      <span className="min-w-0 font-semibold">{label}</span>
      <span className="tc-dots" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
    </div>
  );
}

function DockMessage({
  message,
  mine,
  time,
  channelId,
  readReceipt,
  onOpenThread,
  onReplyInChannel,
}: {
  message: ChannelMessage;
  mine: boolean;
  time: string;
  channelId: string;
  readReceipt?: DockReadReceipt;
  /** Mini-hilo del dock: presente sólo en el feed del canal (las replies
   *  dentro del hilo no lo pasan → sin chip anidado). */
  onOpenThread?: (parentMessageId: string) => void;
  onReplyInChannel?: (message: ChannelMessage) => void;
}) {
  const name = message.user_name || richChatText("view.shared.dashboard.user.unnamed");
  // TC-29: client-only optimistic states (only ever on OWN messages).
  const isPendingMsg = !!message.pending;
  const isFailedMsg = !!message.failed;
  const togglePin = useTogglePin();
  const toggleReaction = useToggleReaction();
  const [menuPoint, setMenuPoint] = React.useState<{ left: number; top: number } | null>(null);

  React.useEffect(() => {
    if (!menuPoint) return;
    const close = () => setMenuPoint(null);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', close, true);
    };
  }, [menuPoint]);

  const openContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    const width = 220;
    const height = 250;
    setMenuPoint({
      left: Math.max(8, Math.min(event.clientX, window.innerWidth - width - 8)),
      top: Math.max(8, Math.min(event.clientY, window.innerHeight - height - 8)),
    });
  };

  const reactWith = (emoji: string) => {
    if (!message.id || isPendingMsg || isFailedMsg) return;
    const existing = (message.reactions ?? []).find((reaction) => reaction.emoji === emoji);
    toggleReaction.mutate({
      messageId: message.id,
      channelId,
      emoji,
      reacted: !!existing?.me,
    });
    setMenuPoint(null);
  };
  // Media caption-less sends carry a single space as `content` (backend
  // requires min=1), so only render the text block when there's real text.
  // Other clients stuff a placeholder label instead ("📌 Nota de voz (5s)") —
  // suppress it too once the attachment itself renders.
  const rawText = (message.content ?? '').trim();
  const text = message.file_url && isMediaPlaceholderCaption(rawText) ? '' : rawText;
  const fwd = message.forwarded_from;
  // Chip "N respuestas / Ver hilo" — visible cuando el mensaje tiene hilo
  // (replies) o tracking; abre el mini-hilo del dock. Nunca en filas
  // optimistas/fallidas (sin id canónico todavía).
  const threadChip =
    onOpenThread &&
    message.id &&
    !isPendingMsg &&
    !isFailedMsg &&
    ((message.reply_count ?? 0) > 0 || message.tracking) ? (
      <button
        type="button"
        onClick={() => onOpenThread(message.id!)}
        className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-blaze-violet hover:underline"
        title={richChatText("view.openThread")}
      >
        <MessageSquareText size={11} className="flex-shrink-0" />
        {(message.reply_count ?? 0) > 0
          ? richChatText('view.repliesCount', { count: message.reply_count ?? 0 })
          : richChatText("view.viewThread")}
        {message.tracking && (
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full flex-shrink-0',
              TRACKING_DOT[message.tracking.status ?? 'open'] ?? 'bg-fg-3',
            )}
            title={statusLabel(message.tracking.status)}
          />
        )}
      </button>
    ) : null;
  const body = (
    <>
      {/* Forwarded-from-inbox provenance: for TEXT forwards the customer's
          words live ONLY in forwarded_from.original_content (m.content is the
          agent's note) — without this quote the forward is context-less in
          the dock (mirror of the ChannelView card, compacted). */}
      {fwd && (
        <div
          className={cn(
            'rounded-xs border-l-2 px-1.5 py-1 text-[11px] leading-snug',
            mine ? 'border-white/60 bg-white/10' : 'border-blaze-violet bg-blaze-violet/10',
          )}
        >
          <span className={mine ? 'opacity-80' : 'text-fg-3'}>{richChatText("view.forwardedFrom")} </span>
          <span className="font-semibold">{fwd.contact_name}</span>
          {fwd.original_content?.trim() ? (
            <span dir="auto" className="block mt-0.5 line-clamp-2 whitespace-pre-wrap">
              “{renderTextWithMentions(fwd.original_content.trim(), { mentions: false })}”
            </span>
          ) : null}
        </div>
      )}
      <DockAttachment message={message} mine={mine} />
      {text.length > 0 && <div dir="auto">{renderTextWithMentions(message.content)}</div>}
    </>
  );
  const contextMenu =
    menuPoint && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="fixed z-[1000] w-[13.25rem] overflow-hidden rounded-sm border border-border-subtle bg-white p-1.5 text-xs text-fg-1 shadow-2xl"
            style={menuPoint}
            role="menu"
            onPointerDown={(event) => event.stopPropagation()}
          >
            {message.id && !isPendingMsg && !isFailedMsg && (
              <div className="mb-1 flex items-center justify-between gap-1 border-b border-border-subtle px-1 pb-1.5">
                {DOCK_EMOJIS.slice(0, 6).map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => reactWith(emoji)}
                    className="flex h-7 w-7 items-center justify-center rounded-xs text-sm hover:bg-paper"
                    title={richChatText('view.reactWith', { emoji })}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
            {onReplyInChannel && !isPendingMsg && !isFailedMsg && (
              <DockContextMenuButton
                icon={<Reply size={13} />}
                onClick={() => {
                  onReplyInChannel(message);
                  setMenuPoint(null);
                }}
              >
                 {richChatText("view.replyChannel")} </DockContextMenuButton>
            )}
            {onOpenThread && message.id && !isPendingMsg && !isFailedMsg && (
              <DockContextMenuButton
                icon={<MessageSquareText size={13} />}
                onClick={() => {
                  onOpenThread(message.id!);
                  setMenuPoint(null);
                }}
              >
                 {richChatText("view.replyThread")} </DockContextMenuButton>
            )}
            {!!rawText && (
              <DockContextMenuButton
                icon={<Copy size={13} />}
                onClick={() => {
                  void navigator.clipboard?.writeText(rawText);
                  setMenuPoint(null);
                }}
              >
                 {richChatText("view.shared.inbox.messageMenu.copy")} </DockContextMenuButton>
            )}
            {message.id && !isPendingMsg && !isFailedMsg && (
              <DockContextMenuButton
                icon={<Pin size={13} />}
                onClick={() => {
                  togglePin.mutate({
                    messageId: message.id!,
                    channelId,
                    pinned: !!message.is_pinned,
                  });
                  setMenuPoint(null);
                }}
              >
                {message.is_pinned ? richChatText("view.unpinMessage") : richChatText("view.pinMessage")}
              </DockContextMenuButton>
            )}
          </div>,
          document.body,
        )
      : null;
  if (mine) {
    return (
      <div className="flex flex-col items-end" onContextMenu={openContextMenu}>
        <div
          className={cn(
            'max-w-[85%] bg-blaze-violet text-white rounded-md rounded-br-xs px-2.5 py-1.5 text-sm whitespace-pre-wrap break-words space-y-1',
            isPendingMsg && 'opacity-60',
            isFailedMsg && 'ring-1 ring-red-500',
          )}
        >
          {body}
        </div>
        <DockReactions message={message} align="end" onReact={reactWith} />
        {threadChip}
        {isPendingMsg ? (
          <span className="flex items-center gap-1 text-[10px] text-fg-3 mt-0.5">
            <Loader2 size={9} className="animate-spin" />
             {richChatText("view.shared.csat.sending")} </span>
        ) : isFailedMsg && message.id ? (
          <FailedMessageActions channelId={channelId} message={message} />
        ) : (
          (time || readReceipt) && (
            <span className="mt-0.5 flex items-center gap-1.5 text-[10px] text-fg-3">
              {time && <span>{time}</span>}
              {readReceipt && (
                <span
                  className={cn(
                    'inline-flex items-center gap-0.5 font-semibold',
                    readReceipt.status === 'read' && 'text-blaze-violet',
                  )}
                  title={readReceipt.title}
                  aria-label={readReceipt.title}
                >
                  {readReceipt.status === 'read' ? <CheckCheck size={11} /> : <Check size={11} />}
                  {readReceipt.label}
                </span>
              )}
            </span>
          )
        )}
        {contextMenu}
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2" onContextMenu={openContextMenu}>
      <span
        className="h-6 w-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 overflow-hidden"
        style={{ background: message.user_avatar ? undefined : colorFromName(name) }}
      >
        {message.user_avatar ? (
          <img src={message.user_avatar} alt="" className="h-full w-full object-cover" />
        ) : (
          initialsFromName(name)
        )}
      </span>
      <div className="min-w-0">
        <div className="flex items-baseline gap-1.5">
          <span className="text-[11px] font-bold text-fg-2 truncate"><bdi dir="auto">{name}</bdi></span>
          {time && <span className="text-[10px] text-fg-3">{time}</span>}
        </div>
        <div className="max-w-[90%] bg-white border border-border-subtle rounded-md rounded-tl-xs px-2.5 py-1.5 text-sm text-fg-1 whitespace-pre-wrap break-words space-y-1">
          {body}
        </div>
        <DockReactions message={message} align="start" onReact={reactWith} />
        {threadChip}
      </div>
      {contextMenu}
    </div>
  );
}

function DockContextMenuButton({
  icon,
  children,
  onClick,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-xs px-2 py-2 text-left font-semibold hover:bg-paper"
    >
      <span className="text-fg-3">{icon}</span>
      {children}
    </button>
  );
}

/**
 * Reaction chips under a dock bubble are live toggles. The context menu adds
 * the quick palette; existing chips can be clicked again to add/remove the
 * current user's reaction through the same mutation as ChannelView.
 */
function DockReactions({
  message,
  align,
  onReact,
}: {
  message: ChannelMessage;
  align: 'start' | 'end';
  onReact: (emoji: string) => void;
}) {
  const reactions = (message.reactions ?? []).filter((r) => !!r.emoji);
  if (reactions.length === 0) return null;
  return (
    <div className={cn('flex flex-wrap gap-1 mt-0.5', align === 'end' && 'justify-end')}>
      {reactions.map((r) => (
        <button
          key={r.emoji}
          type="button"
          onClick={() => onReact(r.emoji)}
          className={cn(
            'inline-flex items-center gap-0.5 rounded-full border px-1.5 py-px text-[11px] leading-4 bg-white hover:border-blaze-violet/60',
            r.me ? 'border-blaze-violet/60' : 'border-border-subtle',
          )}
          title={richChatNames((r.users ?? []).map((u) => u.name))}
        >
          <span>{r.emoji}</span>
          {(r.count ?? 1) > 1 && <span className="text-fg-3 font-semibold">{richChatCount(r.count ?? 0)}</span>}
        </button>
      ))}
    </div>
  );
}

function DockAttachment({ message, mine }: { message: ChannelMessage; mine: boolean }) {
  if (!message.file_url) return null;
  return <TeamChatMedia message={message} compact inverse={mine} />;
}
